import { lazy, type ComponentType } from 'react'
import { isLeavingPageOnceSettled, isReloadBlocked } from '@/shared/lib/staleBuildDetector'

/**
 * Sentinel preventing infinite reload loops if recovery doesn't dislodge
 * the stale cache (network outage, server misconfig, etc.). Cleared on
 * any successful chunk load so a *later* deploy in the same long-lived
 * tab can also self-heal.
 */
const CHUNK_RELOAD_KEY = 'sf-chunk-reload-attempted'

function isStaleChunkError(err: unknown): boolean {
  const msg = (err as { message?: string })?.message || ''
  // Vite/esbuild → "Failed to fetch dynamically imported module"
  // Webpack-era    → "Loading chunk N failed" / "Loading CSS chunk failed"
  // Safari extra   → "Importing a module script failed"
  // NOTE: the "named export missing after successful fetch" case (server
  // 404-fallback returned HTML parsed as a module with no exports) is
  // handled separately in the .then() branch — it produces an undefined
  // export, not a throwable error, so there's no message to match here.
  return /Failed to fetch dynamically imported module|Loading (?:CSS )?chunk \d+ failed|Importing a module script failed/i.test(msg)
}

/**
 * Reload that actually breaks the SW + HTTP cache cycle. Plain
 * window.location.reload() goes through the SW, which can keep serving
 * the stale HTML that's pointing at chunks that no longer exist —
 * exactly the Safari loop in prod after a deploy. Unregister every SW
 * and delete every CacheStorage entry FIRST, then reload, so the new
 * navigation fetches fresh HTML over the network.
 *
 * Best-effort: if any of these APIs are unavailable (older browsers,
 * privacy modes), fall back to a plain reload — better some recovery
 * attempt than none. Catches are deliberately silent — the next step
 * is reload anyway.
 */
async function forceFreshReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister().catch(() => false)))
    }
  } catch { /* ignore */ }

  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k).catch(() => false)))
    }
  } catch { /* ignore */ }

  window.location.reload()
}

/**
 * The promise `lazy()` wraps — factored out of {@link lazyWithRecovery} so a
 * test can drive it directly instead of reaching into React's lazy internals.
 *
 * If the dynamic import fails with a stale-chunk signature AND we haven't
 * already tried recovering in this tab, unregister all SWs, clear
 * CacheStorage, and force-reload — UNLESS a registered stale-build reload
 * blocker (IN-163: the upload queue) is busy, in which case this must NOT
 * reload: it rethrows instead, so the error reaches the nearest ErrorBoundary,
 * which goes through the exact same `recoverFromStaleBuild()`/deferred-banner
 * path a `vite:preloadError` does. Two recovery mechanisms racing to reload the
 * same busy tab is exactly the bug IN-163 exists to close — there must be ONE
 * decision-maker, and `recoverFromStaleBuild` is it.
 */
function resolveWithRecovery<T>(
  loader: () => Promise<{ default: T } | Record<string, T>>,
  exportName?: string,
): Promise<{ default: T }> {
  return loader()
    .then(mod => {
      // Any successful load means we've made it past the stale state.
      // Clear the sentinel so a future deploy in this long-lived tab
      // can also self-heal.
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      // A successful-looking import can still yield a nullish module: the
      // server's SPA catch-all returned index.html for a stale chunk URL, or
      // an empty/partial chunk was served. Guard against `mod` being null/
      // undefined so we route into the existing stale-chunk recovery below
      // instead of throwing a raw "Cannot read properties of undefined"
      // TypeError that crashes the route (observed on /calendar in prod).
      const resolved = mod == null
        ? undefined
        : exportName
          ? (mod as Record<string, T>)[exportName]
          : (mod as { default: T }).default
      if (!resolved) {
        // Module fetched successfully but the requested export is missing.
        // In a deployed SPA this is almost always a stale-chunk symptom:
        // the old chunk URL no longer exists and the server's catch-all
        // 404 → index.html fallback returned HTML, which parses as a
        // module with no exports. Real typos surface in CI / local dev
        // before they ever ship. Treat as stale-chunk + recover.
        const missingExportError = new Error(
          `lazyWithRecovery: export "${exportName ?? 'default'}" missing or module empty/undefined after dynamic import`,
        )
        if (isReloadBlocked()) throw missingExportError
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
          forceFreshReload()
          // Never-resolving promise so Suspense holds the fallback.
          return new Promise<{ default: T }>(() => {})
        }
        throw missingExportError
      }
      return { default: resolved }
    })
    .catch(async err => {
      // IN-189: a navigation away cancels this import, and WebKit reports it as
      // "Importing a module script failed" — sometimes before the page's own
      // `beforeunload`. Wait for that to settle; if the page is being left, a
      // reload here would re-open it and cancel the user's navigation, so hold
      // the Suspense fallback while it goes.
      if (isStaleChunkError(err) && await isLeavingPageOnceSettled()) {
        return new Promise<{ default: T }>(() => {})
      }
      if (isStaleChunkError(err) && !isReloadBlocked()
          && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
        forceFreshReload()
        // Never-resolving promise so React Suspense doesn't tear
        // down the fallback before the navigation kicks in.
        return new Promise<{ default: T }>(() => {})
      }
      throw err
    })
}

/**
 * Wraps React.lazy with stale-chunk recovery. See {@link resolveWithRecovery}
 * for the recovery policy.
 *
 * Supports both default-export and named-export modules:
 *   lazyWithRecovery(() => import('./X'))                    // default export
 *   lazyWithRecovery(() => import('./X'), 'NamedExport')     // named export
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRecovery<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T } | Record<string, T>>,
  exportName?: string,
): T {
  // Cast through unknown: lazy() returns LazyExoticComponent<T> which is
  // structurally compatible with T at every call site — callers keep their
  // full prop types. The cast is safe because React.lazy is the implementation.
  return lazy(() => resolveWithRecovery(loader, exportName)) as unknown as T
}

/**
 * Test-only introspection — lets a test drive the recovery promise directly
 * instead of reaching into React.lazy's internals. Same rationale as
 * uploadStore's `__uploadInternals`. Tree-shaken from app code (unreferenced
 * there).
 */
export const __lazyWithRecoveryInternals = { resolveWithRecovery }
